<script setup>
import Slider from '@vueform/slider';
import '@vueform/slider/themes/default.css';
import SwitchButtonGroup from '../../common/SwitchButtonGroup.vue';
import dcConst from './DraughtControl.constantes.js';
import { parseSafetyContour, round, countDecimals } from '@/plugins/utils/helpers.js';
import { inject, ref, reactive, computed, watch, onMounted } from 'vue';
import { usePortAllData } from '@/stores/portAllData';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { LIGHT_CONDITION } from '@/plugins/utils/constantes';
import draughtIconDay from '@/assets/images/draughtControl/draught_DAY.svg';
import draughtIconNight from '@/assets/images/draughtControl/draught_NIGHT.svg';
import draughtIconDusk from '@/assets/images/draughtControl/draught_DUSK.svg';
import soundingIconDay from '@/assets/images/draughtControl/sounding_DAY.svg';
import soundingIconNight from '@/assets/images/draughtControl/sounding_NIGHT.svg';
import soundingIconDusk from '@/assets/images/draughtControl/sounding_DUSK.svg';
import tideIconDay from '@/assets/images/draughtControl/tide_DAY.svg';
import tideIconNight from '@/assets/images/draughtControl/tide_NIGHT.svg';
import tideIconDusk from '@/assets/images/draughtControl/tide_DUSK.svg';

const { t } = useI18n();

const props = defineProps({
  draughtTideOptions: {
    type: Object,
    default: () => {
      return {}
    }
  }
})

const $map = inject('$map');

const store = usePortAllData();
const { getLayerExtraParameters, getIsMobile, getLayerColor } = storeToRefs(store);
const { updateLayerExtraParameters, setContours } = store;

const draughtMin = ref(0);
const draughtMax = ref(10);
const draughtStep = ref(0.1);
const draughtDecimals = ref(1);
const tideMin = ref(0);
const tideMax = ref(10);
const tideStep = ref(0.1);
const tideDecimals = ref(1);
const soundingMin = ref(0);
const soundingMax = ref(10);
const soundingStep = ref(0.1);
const soundingDecimals = ref(1);

const state = ref(null);
const maregrapheActivated = ref(false);
const inhibited = ref(false);
const visibilityBeforeInhibition = reactive([]);
const layersToInhibit = reactive([]);

const buttons = reactive([]);

const draughtValue = ref(0);
const tideValue = ref(0);
const soundingValue = ref(0);

const sliderSettings = {
  tide: props.draughtTideOptions.tide,
  draught: props.draughtTideOptions.draught,
  soundings: props.draughtTideOptions.soundings,
}

const draughtIcon = ref(draughtIconDay)
const soundingIcon = ref(soundingIconDay)
const tideIcon = ref(tideIconDay)

onMounted(() => {
  for (const layer of $map.getLayers().getArray()) {
    if (layer.getName && props.draughtTideOptions.layersToInhibit.includes(layer.getName())) {
      layersToInhibit.push(layer);
      visibilityBeforeInhibition.push(layer.getVisible());
    }
  }

  initButtons()

  if (props.draughtTideOptions.maregraphe) {
    // jsonHttpGet('./maregraphes').then((json) => {
    //   const optionList = json.reduce((prev, curr) => {
    //     prev.push({
    //       label: curr.name,
    //       value: curr.shom_id,
    //     });
    //     return prev;
    //   }, [{ label: 'Désactivé', value: -1, selected: true }]);
    //   const selectButton = new SelectButton({
    //     prefix: 'Maregraphe',
    //     optionList,
    //   });
    //   selectButton.on(SelectButtonEventType.CHANGE, (event) => {
    //     if (event.value > 0) {
    //       const startDate = new Date(Date.now() - 86400000).toISOString();
    //       const endDate = new Date(Date.now()).toISOString();
    //       const url = `./maregraphes/${event.value}?sources=1&dtStart=${startDate}&dtEnd=${endDate}&interval=1`;
    //       jsonHttpGet(url).then((_json) => {
    //         const { data } = _json;
    //         if (data.length > 0) {
    //           buttonGroup.setPositionByKey(TIDE_LABEL);
    //           this.state_ = TIDE_STATE;
    //           this.setValue_(data[data.length - 1].value);
    //           this.maregrapheActivated = true;
    //         }
    //       });
    //     }
    //   });
    //   buttonDiv.appendChild(selectButton.dom);
    // });
  }

  updateView(dcConst.DRAUGHT_STATE);
  updateLayerExtraParameters([{ paramKey: dcConst.SAFETY_DEPTH, paramValue: soundingValue.value }]);
  const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
  setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
})

const initButtons = () => {
  if (buttons.length > 0) buttons.length = 0;
  setIconColor()
  const contours = parseSafetyContour(getLayerExtraParameters.value[dcConst.SAFETY_CONTOUR]);
  if (sliderSettings.draught) {
    draughtMin.value = sliderSettings.draught.min || 0;
    draughtMax.value = sliderSettings.draught.max;
    draughtStep.value = sliderSettings.draught.step;
    draughtValue.value = contours[1];
    if (Number.isNaN(draughtValue.value) || draughtValue.value === undefined) draughtValue.value = draughtMin.value
    draughtDecimals.value = countDecimals(sliderSettings.draught.step);   
    buttons.push({
      text: formatButtonLabel(t('draught'), draughtValue.value),
      img: draughtIcon.value,
      key: dcConst.DRAUGHT_LABEL,
      callback: () => {
        updateView(dcConst.DRAUGHT_STATE);
      }
    })
  }
  if (sliderSettings.tide) {
    tideMin.value = sliderSettings.tide.min || 0;
    tideMax.value = sliderSettings.tide.max;
    tideStep.value = sliderSettings.tide.step;
    tideDecimals.value = countDecimals(sliderSettings.tide.step);
    tideValue.value = round(contours[1] - contours[0], tideDecimals.value);
    if (Number.isNaN(tideValue.value) || tideValue.value === undefined) tideValue.value = tideMin.value
    buttons.push({
      text: formatButtonLabel(t('tide'), tideValue.value),
      img: tideIcon.value,
      key: dcConst.TIDE_LABEL,
      callback: () => {
        updateView(dcConst.TIDE_STATE);
      }
    })
  }
  if (sliderSettings.soundings) {
    soundingMin.value = sliderSettings.soundings.min || 0;
    soundingMax.value = sliderSettings.soundings.max;
    soundingStep.value = sliderSettings.soundings.step;
    soundingValue.value = getLayerExtraParameters.value.SAFETY_DEPTH;
    soundingDecimals.value = countDecimals(sliderSettings.soundings.step);
    if (Number.isNaN(soundingValue.value) || soundingValue.value === undefined) soundingValue.value = soundingMin.value
    buttons.push({
      text: formatButtonLabel(t('sounding'), soundingValue.value),
      img: soundingIcon.value,
      key: dcConst.SOUNDINGS_LABEL,
      callback: () => {
        updateView(dcConst.SOUNDINGS_STATE);
      }
    })
  }
}

const setIconColor = () => {
  switch (getLayerColor.value) {
    case LIGHT_CONDITION.DUSK:
      draughtIcon.value = draughtIconDusk
      soundingIcon.value = soundingIconDusk
      tideIcon.value = tideIconDusk
      break
    case LIGHT_CONDITION.NIGHT:
      draughtIcon.value = draughtIconNight
      soundingIcon.value = soundingIconNight
      tideIcon.value = tideIconNight
      break
    case LIGHT_CONDITION.DAY:
    default:
      draughtIcon.value = draughtIconDay
      soundingIcon.value = soundingIconDay
      tideIcon.value = tideIconDay
      break
  }
}

const activeButtonIndex = computed(() => {
  let key = null;
  switch (state.value) {
    case dcConst.DRAUGHT_STATE:
      key = dcConst.DRAUGHT_LABEL;
      break;
    case dcConst.TIDE_STATE:
      key = dcConst.TIDE_LABEL;
      break
    case dcConst.SOUNDINGS_STATE:
      key = dcConst.SOUNDINGS_LABEL;
      break;
  }
  if (key) return buttons.findIndex(b => b.key === key);
  return 0;
});

const sliderDraughtInput = function(val) {
  if (!inhibited.value) {
    inhibited.value = true;
    visibilityBeforeInhibition.length = 0;
    for (const layer of layersToInhibit) {
      visibilityBeforeInhibition.push(layer.getVisible());
    }
  }
  draughtValue.value = round(val, draughtDecimals.value);
  const _label = t('draught')
  buttons[activeButtonIndex.value].text = formatButtonLabel(_label, draughtValue.value);
  const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
  setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
}

const sliderTideInput = function(val) {
  if (!inhibited.value) {
    inhibited.value = true;
    visibilityBeforeInhibition.length = 0;
    for (const layer of layersToInhibit) {
      visibilityBeforeInhibition.push(layer.getVisible());
    }
  }
  tideValue.value = round(val, tideDecimals.value);
  const _label = t('tide')
  buttons[activeButtonIndex.value].text = formatButtonLabel(_label, tideValue.value);
  const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);  
  setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
}

const sliderSoundingInput = function(val) {
  if (!inhibited.value) {
    inhibited.value = true;
    visibilityBeforeInhibition.length = 0;
    for (const layer of layersToInhibit) {
      visibilityBeforeInhibition.push(layer.getVisible());
    }
  }
  soundingValue.value = round(val, soundingDecimals.value);
  const _label = t('sounding')
  buttons[activeButtonIndex.value].text = formatButtonLabel(_label, soundingValue.value);
  updateLayerExtraParameters([{ paramKey: dcConst.SAFETY_DEPTH, paramValue: soundingValue.value }]);  
}

const sliderChange = function() {
  if (inhibited.value) {
    inhibited.value = false;
    for (let i = 0; i < layersToInhibit.length; i++) {
      layersToInhibit[i].setVisible(visibilityBeforeInhibition[i]);
    }
  }
}

const updateView = function(_state) {
  state.value = _state;
}

const increaseValue = function() {
  switch (state.value) {
    case dcConst.DRAUGHT_STATE: {
      const max = sliderSettings.draught.max || 10;
      const step = sliderSettings.draught.step || 0.1;
      let newValue = draughtValue.value + step;
      if (newValue > max) newValue = max;
      draughtValue.value = newValue;
      const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
      setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
      break;
    }
    case dcConst.TIDE_STATE: {
      const max = sliderSettings.tide.max || 10;
      const step = sliderSettings.tide.step || 0.1
      let newValue = tideValue.value + step;
      if (newValue > max) newValue = max;
      tideValue.value = newValue;
      const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
      setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
      break;
    }
    case dcConst.SOUNDINGS_STATE: {
      const max = sliderSettings.soundings.max || 10;
      const step = sliderSettings.soundings.step || 0.1;
      let newValue = soundingValue.value + step;
      if (newValue > max) newValue = max;
      soundingValue.value = newValue;
      break;
    }
  }
  for (let i = 0; i < layersToInhibit.length; i++) {
    if (visibilityBeforeInhibition.length && visibilityBeforeInhibition[i]) {
      layersToInhibit[i].setVisible(visibilityBeforeInhibition[i]);
    }
  }
}

const decreaseValue = function() {
  switch (state.value) {
    case dcConst.DRAUGHT_STATE: {
      const min = sliderSettings.draught.min || 0;
      const step = sliderSettings.draught.step || 0.1;
      let newValue = draughtValue.value - step;
      if (newValue < min) newValue = min;
      draughtValue.value = newValue;
      const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
      setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
      break;
    }
    case dcConst.TIDE_STATE: {
      const min = sliderSettings.tide.min || 0;
      const step = sliderSettings.tide.step || 0.1
      let newValue = tideValue.value - step;
      if (newValue < min) newValue = min;
      tideValue.value = newValue;
      const roundSafetyContour = round(draughtValue.value - tideValue.value, 1);
      setContours([roundSafetyContour, draughtValue.value, roundSafetyContour]);
      break;
    }
    case dcConst.SOUNDINGS_STATE: {
      const min = sliderSettings.soundings.min || 10;
      const step = sliderSettings.soundings.step || 0.1;
      let newValue = soundingValue.value - step;
      if (newValue < min) newValue = min;
      soundingValue.value = newValue;
      break;
    }
  }
  for (let i = 0; i < layersToInhibit.length; i++) {
    if (visibilityBeforeInhibition.length && visibilityBeforeInhibition[i]) layersToInhibit[i].setVisible(visibilityBeforeInhibition[i]);
  }
}

const slideOrientation = computed(() => {
  if (getIsMobile.value) return 'vertical';
  return 'horizontal';
})

const slideDirection = computed(() => {
  if (getIsMobile.value) return 'rtl';
  return 'ltr';
})

const formatButtonLabel = (label, value) => {
  if (getIsMobile.value) return value;
  return `${label}: ${value}`;
}

watch(getIsMobile, () => {
  const _labelDraught = t('draught')
  buttons[0].text = formatButtonLabel(_labelDraught, draughtValue.value);
  const _labelTide = t('tide')
  buttons[1].text = formatButtonLabel(_labelTide, tideValue.value);
  const _labelSounding = t('sounding')
  buttons[2].text = formatButtonLabel(_labelSounding, soundingValue.value);
})

watch(getLayerColor, () => {
  initButtons()
})
</script>

<template>
  <div class="draught-control">
    <switch-button-group class="type-slider-switcher" :buttons="buttons" :position="activeButtonIndex" />
    <div class="slider-with-button">
      <button @click="decreaseValue">
        <i class="icon-minus" />
      </button>
      <Slider
        v-if="state === dcConst.DRAUGHT_STATE"
        v-model="draughtValue"
        class="slider-draught"
        :min="draughtMin"
        :max="draughtMax"
        :step="draughtStep"
        :tooltips="false"
        :lazy="false"
        :orientation="slideOrientation"
        :direction="slideDirection"
        @update="sliderDraughtInput"
        @change="sliderChange"
      />
      <Slider
        v-else-if="state === dcConst.TIDE_STATE"
        v-model="tideValue"
        class="slider-draught"
        :min="tideMin"
        :max="tideMax"
        :step="tideStep"
        :tooltips="false"
        :lazy="false"
        :orientation="slideOrientation"
        :direction="slideDirection"
        @update="sliderTideInput"
        @change="sliderChange"
      />
      <Slider
        v-else-if="state === dcConst.SOUNDINGS_STATE"
        v-model="soundingValue"
        class="slider-draught"
        :min="soundingMin"
        :max="soundingMax"
        :step="soundingStep"
        :tooltips="false"
        :lazy="false"
        :orientation="slideOrientation"
        :direction="slideDirection"
        @update="sliderSoundingInput"
        @change="sliderChange"
      />
      <button @click="increaseValue">
        <i class="icon-plus" />
      </button>
    </div>
  </div>
</template>

<i18n src="@/locales/draughtControl.json"></i18n>